import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import {
  QuestionLayout,
  IconInput,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useNavigation } from "@react-navigation/native";
import { MoreHorizontal } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type { MedicationsVaccinesStackScreenProps } from "../MedicationsVaccinesNavigator";
import {
  MedicationProductType,
  useMedicationsVaccines,
} from "../MedicationsVaccinesContext";
import { MEDICATION_PRODUCT_OPTIONS } from "../options";

export function MedicationsProductsScreen() {
  const t = useTranslations();
  const { state, updateState } = useMedicationsVaccines();
  const navigation =
    useNavigation<
      MedicationsVaccinesStackScreenProps<"MedicationsProducts">["navigation"]
    >();
  const selected = useMemo(
    () => new Set(state.selectedProducts || []),
    [state.selectedProducts],
  );

  const toggleSelection = (product: MedicationProductType) => {
    const newSelection = new Set(selected);
    if (newSelection.has(product)) {
      newSelection.delete(product);
    } else {
      newSelection.add(product);
    }

    let next = Array.from(newSelection) as MedicationProductType[];
    if (next.includes("none")) {
      next = ["none"];
    } else {
      next = next.filter((v) => v !== "none");
    }
    updateState({ selectedProducts: next });
  };

  const handleSave = () => {
    const selectedArray = Array.from(selected);
    if (selectedArray.includes("none") || selectedArray.length === 0) {
      // When "none" is selected, track all existing medication IDs for deletion
      const allExistingMedicationIds = state.medicationDetails
        .filter((detail) => !!detail.id)
        .map((detail) => detail.id!)
        .filter((id) => !(state.medicationIdsToDelete || []).includes(id)); // Avoid duplicates

      updateState({
        currentlyTakingMedications: false,
        selectedProducts: ["none"],
        medicationDetails: [], // Clear all
        medicationIdsToDelete: [
          ...(state.medicationIdsToDelete || []),
          ...allExistingMedicationIds,
        ],
      });
      navigation.navigate("VaccinesSelect");
      return;
    }

    updateState({ currentlyTakingMedications: true });
    navigation.navigate("MedicationDetails");
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.medications_vaccines.products_title")}
      subtitle={t("med_history.medical.medications_vaccines.products_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selected.size === 0}
      saveLabel={t("med_history.medical.shared.continue")}
    >
      <View style={styles.optionsGrid}>
        {MEDICATION_PRODUCT_OPTIONS.map((option) => {
          const isSelected = selected.has(option.id);
          return (
            <View key={option.id} style={styles.optionWrapper}>
              <SquareOptionButton
                emoji={option.emoji}
                label={t(option.labelKey)}
                isSelected={isSelected}
                onPress={() => toggleSelection(option.id)}
                labelSize="sm"
                style={styles.optionButton}
              />
            </View>
          );
        })}
      </View>
      {selected.has("other") && !selected.has("none") && (
        <View style={styles.otherInput}>
          <IconInput
            icon={MoreHorizontal}
            label={t(
              "med_history.medical.medications_vaccines.other_medication_label",
            )}
            placeholder={t(
              "med_history.medical.medications_vaccines.other_medication_placeholder",
            )}
            value={state.otherMedicationText}
            onChangeText={(text) => updateState({ otherMedicationText: text })}
          />
        </View>
      )}
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
  optionButton: {
    minHeight: 160,
    padding: 24,
    borderRadius: 16,
  },
  otherInput: {
    width: "100%",
    marginTop: -28,
    marginBottom: 40,
  },
});

export default MedicationsProductsScreen;
